"use client"

import { useState, useCallback, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { currencyOptions } from "../utils/currencyOptions"
import { api, getAuthHeaders, createReminder, updateReminder, deleteReminder } from "../utils/api"
import { uploadToS3 } from "../utils/uploadToS3"
import { showSuccessToast, showErrorToast } from "../components/Toast"
import Loader from "../components/Loader"
import { useDispatch } from "react-redux"
import { createAsset } from "../redux/assetSlice"
import { useUser } from "../redux/useUser"
import { useIsMobile } from "../hooks/useIsMobile"
import Layout from "../components/Layout"
import { Formik, Form as FormikForm, ErrorMessage } from "formik"
import { Form, Modal, Button } from "react-bootstrap"


import * as Yup from "yup"
import Select from "react-select"
import { useDropzone } from "react-dropzone"
import { getValidationSchema } from "../utils/validationSchemas"
import { fillExtractedFields } from "../utils/assetFormUtils"
import { getFieldConfig } from "../components/asset-forms/config/fieldConfigs"
import "../styles/assets-liability.scss"

// Import form components (validation schemas now centralized in utils/validationSchemas.js)
import InsuranceForm from "../components/asset-forms/insurance-form.js"
import VehicleForm from "../components/asset-forms/vehicle-form.js"
import CashForm from "../components/asset-forms/cash-form.js"
import PropertyForm from "../components/asset-forms/property-form.js"
import BankAccountsForm from "../components/asset-forms/bank-accounts-form"
import MutualFundsForm from "../components/asset-forms/mutual-funds-form.js"
import FixedDepositForm from "../components/asset-forms/fixed-deposit-form.js"
import StocksForm from "../components/asset-forms/stocks-form.js"
import CryptoForm from "../components/asset-forms/crypto-form.js"
import CollectiblesForm from "../components/asset-forms/collectibles-form.js"
import BusinessOwnershipForm from "../components/asset-forms/business-ownership-form.js"
import OtherPhysicalAssetsForm from "../components/asset-forms/other-physical-assets-form.js"
import ImportantPapersForm from "../components/asset-forms/important-papers-form.js"
import PreciousMetalsForm from "../components/asset-forms/precious-metals-form.js"
import RightSidebar from "../components/asset-forms/common/RightSidebar"
import SetReminderSection from "../components/asset-forms/common/SetReminderSection"

// Subcategory options mapped by asset type
const subCategoryOptionsByAssetType = {
  digital: [
    { value: "insurance", label: "Insurance" },
    { value: "bank accounts", label: "Bank Accounts", apiValue: "bankAccounts" },
    { value: "mutual funds", label: "Mutual Funds", apiValue: "mutualFunds" },
    { value: "fixed deposit", label: "Fixed Deposit", apiValue: "fixedDeposits" },
    { value: "stocks", label: "Stocks" },
    { value: "crypto", label: "Crypto" },
    { value: "business ownership", label: "Business Ownership / Equity", apiValue: "businessOwnership" }, // new digital
    { value: "important papers", label: "Important Papers", apiValue: "importantPapers" }, // new digital
  ],
  physical: [
    { value: "cash", label: "Cash" },
    { value: "property", label: "Property" },
    { value: "vehicle", label: "Vehicle" },
    { value: "collectibles", label: "Collectibles" }, // new physical
    { value: "precious metals", label: "Precious Metals" }, // new physical
    { value: "other physical assets", label: "Other Physical Assets" }, // new physical
  ],
}
const normalizeExtractedItem = (raw, subCategory) => {
  if (!raw || typeof raw !== "object") return {}

  const base = raw?.data?.data || raw?.data || raw || {}

  const detailMap = {
    insurance: base.insuranceDetails,
    vehicle: base.vehicleDetails,
    crypto: base.cryptoDetails,
    stocks: base.stockDetails,
    "mutual funds": base.mutualFundDetails,
    "fixed deposit": base.fixedDepositDetails,
    "bank accounts": base.bankAccountDetails,
    property: base.propertyDetails,
  }

  const details = detailMap[subCategory]

  if (Array.isArray(details)) {
    return details.map((d) => ({ ...base, ...d }))
  }

  if (details && typeof details === "object") {
    return { ...base, ...details }
  }

  return base
}

const normalize = (v = "") => v.toLowerCase().replace(/\s+/g, "")

const EXTRACTION_KEY_MAP = {
  insurance: ["insurance", "insuranceDetails"],
  "bank accounts": ["bankAccounts", "bankAccountDetails"],
  "mutual funds": ["mutualFunds", "mutualFundDetails"],
  "fixed deposits": ["fixedDeposits", "fixedDepositDetails"],
  stocks: ["stocks", "stockDetails"],
  crypto: ["crypto", "cryptoDetails"],
  vehicle: ["vehicles", "vehicleDetails"],
  property: ["property", "propertyDetails"],
  collectibles: ["collectibles", "collectiblesDetails"],
  "business ownership": ["businessOwnership", "businessOwnershipDetails"],
  "other physical assets": ["otherPhysicalAssets", "otherPhysicalAssetsDetails"],
  "important papers": ["importantPapers", "importantPapersDetails"],
  "precious metals": ["preciousMetals", "preciousMetalDetails"],
}
const getExtractedArrayForSubCategory = (extractedData, subCategory) => {
  if (!extractedData || !subCategory) return null

  const possibleKeys = EXTRACTION_KEY_MAP[subCategory] || []

  for (const key of possibleKeys) {
    if (Array.isArray(extractedData[key])) {
      return extractedData[key]
    }

    if (extractedData[key] && typeof extractedData[key] === "object") {
      return [extractedData[key]]
    }
  }

  if (subCategory === "bank accounts" && extractedData.accountNumber) {
    return [extractedData]
  }

  return null
}

// Liability options for search
const liabilityOptions = [
  "Insurance",
  "Services",
  "Mortgage",
  "Credit Card Debt",
  "Personal Loan",
  "Auto Loan",
  "Student Loan",
  "Medical Bills",
  "Utilities",
  "Rent",
  "Property Tax",
  "Income Tax",
  "Legal Fees",
  "Maintenance",
  "Subscription Services",
]

// Main form validation schema (only for step 1 and step 3 fields)
const addAssetsSchema = Yup.object().shape({
  assetType: Yup.string().required("This field is required"),
  subCategory: Yup.string().required("This field is required"),
  setReminder: Yup.string().required("This field is required"),
  notes: Yup.string()
    .max(500, "Notes must be less than 500 characters")
    .test(
      "valid-notes",
      "Notes contain invalid or unsafe content.",
      value => {
        if (!value) return true;
        // Disallow script, iframe, img, inline JS, SQL patterns
        const forbiddenPatterns = [
          /<script.*?>/i,
          /<iframe.*?>/i,
          /<img.*?onerror=.*?>/i,
          /on\w+\s*=\s*['"].*?['"]/i, // inline JS events
          /(['"]).*?\b(SELECT|INSERT|UPDATE|DELETE|DROP|ALTER|CREATE|TRUNCATE|EXEC|UNION|--|;|\bOR\b|\bAND\b).*?\1/i, // SQLi
        ];
        for (const pattern of forbiddenPatterns) {
          if (pattern.test(value)) return false;
        }
        // Allow printable chars, common punctuation, symbols, newlines, URLs
        // Disallow control chars except \n, \r, \t
        const printable = /^[\x20-\x7E\n\r\t]*$/;
        if (!printable.test(value)) return false;
        return true;
      }
    ),
  isActive: Yup.string().required("Please select if this is active"),
})

// Function to get merged validation schema - now uses centralized utility
function getMergedValidationSchema(subCategory) {
  // Use centralized validation schema from utils (validates base + subcategory fields)
  return getValidationSchema(subCategory)
}

// Function to get initial values for subcategory
const getSubCategoryInitialValues = (subCategory) => {
  const assetInitialValues = {
    insurance: require("../components/asset-forms/insurance-form.js").insuranceInitialValues,
    vehicle: require("../components/asset-forms/vehicle-form.js").vehicleInitialValues,
    cash: require("../components/asset-forms/cash-form.js").cashInitialValues,
    property: require("../components/asset-forms/property-form.js").propertyInitialValues,
    "bank accounts": {
      ...require("../components/asset-forms/bank-accounts-form").bankAccountsInitialValues,
      ownershipType: "", // Add required field
      providerName: "", // Add required field
    },
    "mutual funds": require("../components/asset-forms/mutual-funds-form.js").mutualFundsInitialValues,
    "fixed deposit": require("../components/asset-forms/fixed-deposit-form.js").fixedDepositInitialValues,
    stocks: require("../components/asset-forms/stocks-form.js").stocksInitialValues,
    crypto: require("../components/asset-forms/crypto-form.js").cryptoInitialValues,
    collectibles: require("../components/asset-forms/collectibles-form.js").collectiblesInitialValues,
    "business ownership": require("../components/asset-forms/business-ownership-form.js")
      .businessOwnershipInitialValues,
    "other physical assets": require("../components/asset-forms/other-physical-assets-form.js")
      .otherPhysicalAssetsInitialValues,
    "important papers": require("../components/asset-forms/important-papers-form.js").importantPapersInitialValues,
    "precious metals": require("../components/asset-forms/precious-metals-form.js").preciousMetalsInitialValues,
  }
  return assetInitialValues[subCategory] || {}
}

const AddAssets = () => {
  // Current form values state (moved to top for useCallback dependency)
  const [currentAssetType, setCurrentAssetType] = useState("digital")
  const [currentSubCategory, setCurrentSubCategory] = useState("insurance")

  // For extracted stocks selection
  const [extractedItems, setExtractedItems] = useState([])
  const [showStockPicker, setShowStockPicker] = useState(false)
  const [fullExtractedData, setFullExtractedData] = useState(null) // Store full API response for merging
  const setFieldValueRef = useRef()
  const navigate = useNavigate()
  // State for detected currency (from IP geolocation)
  const [detectedCurrency, setDetectedCurrency] = useState(null)
  // Track last selected currency to persist across subcategory/type changes
  const [selectedCurrency, setSelectedCurrency] = useState(null)
  // Add formKey state to force Formik remount
  const [formKey, setFormKey] = useState(0)


  // On mount: Use IP geolocation API to set currency if not set
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Try ipapi.co first, fallback to ipify + freeipapi if CORS fails
      fetch("https://ipapi.co/json/")
        .then((res) => res.json())
        .then((data) => {
          if (data && data.currency) {
            setDetectedCurrency(data.currency)
          }
        })
        .catch(() => {
          // Fallback to freeipapi.com (no CORS issues)
          fetch("https://freeipapi.com/api/json")
            .then((res) => res.json())
            .then((data) => {
              if (data && data.currency && data.currency.code) {
                setDetectedCurrency(data.currency.code)
              }
            })
            .catch(() => {
              // If both fail, just use default INR
              setDetectedCurrency("INR")
            })
        })
    }
  }, [])

  // Set initial currency when detected
  useEffect(() => {
    if (detectedCurrency && !selectedCurrency) {
      const currencyToUse = currencyOptions.some((opt) => opt.value === detectedCurrency) ? detectedCurrency : "INR"
      setSelectedCurrency(currencyToUse)
    }
  }, [detectedCurrency, selectedCurrency])

  // Get current initial values based on state
  const getCurrentFormInitialValues = () => {
    const currencyToUse =
      selectedCurrency ||
      (detectedCurrency && currencyOptions.some((opt) => opt.value === detectedCurrency) ? detectedCurrency : "INR")
    const subCategoryInitials = getSubCategoryInitialValues(currentSubCategory)
    // Set purchaseDate to today if not present
    const today = new Date().toISOString().slice(0, 10)
    return {
      assetType: currentAssetType,
      subCategory: currentSubCategory,
      name: "",
      purchasePrice: "",
      setReminder: "no",
      notes: "",
      isActive: "yes",
      purchaseCurrency: currencyToUse,
      linkedLiability: [],
      ...subCategoryInitials,
      // If subCategoryInitials.purchaseDate is empty or undefined, use today
      purchaseDate: subCategoryInitials.purchaseDate || today,
    }
  }

  const [fileUploading, setFileUploading] = useState(false)
  const [extractLoading, setExtractLoading] = useState(false)
  const [extractMessage, setExtractMessage] = useState("")
  const [fileProgresses, setFileProgresses] = useState({})
  const [fileErrorMessages, setFileErrorMessages] = useState({})

  const dispatch = useDispatch()
  const { user } = useUser()
  const [uploadedDocuments, setUploadedDocuments] = useState([])
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [tags, setTags] = useState([])
  const [newTag, setNewTag] = useState("")
  const [isSaved, setIsSaved] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const isMobile = useIsMobile()
  const [reminders, setReminders] = useState([])
  const [showReminderSection, setShowReminderSection] = useState("no")
  const [remindersCollapsed, setRemindersCollapsed] = useState(false)
  const [editingReminderId, setEditingReminderId] = useState(null)
  const [reminderForm, setReminderForm] = useState({
    description: "",
    type: "Monthly",
    dayOfMonth: "1",
    monthOfYear: "1",
  })


  // Use the shared uploadToS3 utility
  const uploadFileToS3 = async (file) => {
    try {
      const s3Response = await uploadToS3(file, {
        user,
        setImageUploading: setFileUploading,
        setProgresses: setFileProgresses,
        showSuccessToast,
        showErrorToast,
        setErrorMessages: setFileErrorMessages,
        updateReduxUser: false,
      })
      let fileUrl = s3Response
      if (s3Response && typeof s3Response === "object") {
        fileUrl = s3Response.fileName || s3Response.url
      }
      setUploadedFiles((prev) => {
        const updated = prev.map((f) => (f.id === file.id ? { ...f, uploadedUrl: fileUrl, type: file.type } : f))
        return updated
      })
    } catch (error) {
      // Error handling is done in utility
    }
  }

  const handleUploadAllFiles = async () => {
    for (const fileObj of uploadedFiles) {
      if (!fileObj.uploadedUrl) {
        await uploadFileToS3(fileObj.file)
      }
    }
  }

  const allFilesUploaded = uploadedFiles.length === 0 || uploadedFiles.every((f) => f.uploadedUrl)

  // Liability management state
  const [liabilities, setLiabilities] = useState([
    { id: 1, name: "Insurance" },
    { id: 2, name: "Services" },
  ])
  const [searchTerm, setSearchTerm] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)
  const [isLiabilitiesCollapsed, setIsLiabilitiesCollapsed] = useState(false)

  // Upload a single document to S3 and call AI extract API
  // NOTE: fillExtractedFields is now imported from utils/assetFormUtils.js
  // It uses config-based field mapping for cleaner code

  const uploadDocumentAndExtract = useCallback(async (file, setFieldValue) => {
    if (!file) return null

 
    const fileObj = {
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
    }
    // optimistic add
    setUploadedDocuments((prev) => [...prev, fileObj])
    try {
      setExtractLoading(true)
      setExtractMessage("")
      const s3Resp = await uploadToS3(file, {
        user,
        setImageUploading: setFileUploading,
        setProgresses: setFileProgresses,
        showSuccessToast,
        showErrorToast,
        setErrorMessages: setFileErrorMessages,
        updateReduxUser: false,
      })
      let s3Url = null
      if (s3Resp) {
        if (typeof s3Resp === "string") s3Url = s3Resp
        else if (typeof s3Resp === "object") s3Url = s3Resp.url || s3Resp.fileName || s3Resp.location || null
      }
      // update local documents list with uploadedUrl
      setUploadedDocuments((prev) => prev.map((d) => (d.id === fileObj.id ? { ...d, uploadedUrl: s3Url } : d)))

      if (s3Url) {
        const payload = { s3Url, type: "asset" }
        const extractRes = await api.post("/ai-extraction/extract", payload, { headers: getAuthHeaders() })
        showSuccessToast("Document uploaded and sent for AI extraction")

        const extractedData = extractRes?.data?.data?.data || {}
        const extractedAssetType = extractedData.assetType
        const purchaseCurrency = extractedData.purchaseCurrency

        // Normalize for comparison (lowercase, remove spaces, handle plural/singular)
        const normalize = (str) => {
          if (!str) return "";
          let normalized = str.toLowerCase().replace(/\s+/g, "");

          // Handle specific mappings
          if (normalized === "bankaccounts") normalized = "bankaccount";

          // Remove trailing 's' for plural forms
          normalized = normalized.replace(/s$/, "");

          return normalized;
        }

        setUploadedDocuments([])


        // 🔥 AUTO-SWITCHING: Check if extracted asset type matches selected category
        if (extractedAssetType && normalize(extractedAssetType) !== normalize(currentSubCategory)) {
          // Asset type mismatch detected - auto-switch silently
          setExtractMessage(`📄 Document is for ${extractedAssetType}. Switching form...`)

          // Find extracted items array for the DETECTED asset type
          const detectedKeys = [`${extractedAssetType}Details`, `${normalize(extractedAssetType)}Details`, extractedAssetType, normalize(extractedAssetType)]
          let detectedArr = null

          for (const key of detectedKeys) {
            if (Array.isArray(extractedData[key])) {
              detectedArr = extractedData[key]
              break
            }
            if (extractedData[key] && typeof extractedData[key] === "object") {
              detectedArr = [extractedData[key]]
              break
            }
          }

          // 🔥 FALLBACK — jab details key hi na ho (mutual fund case)
          if (!detectedArr || detectedArr.length === 0) {
            const normalized = normalizeExtractedItem(extractRes?.data, extractedAssetType)
            if (Object.keys(normalized).length > 0) {
              detectedArr = [normalized]
            }
          }

          // Store extracted data for auto-fill after switch
          if (detectedArr && detectedArr.length > 0) {
            setExtractedItems(detectedArr)
            setFullExtractedData(extractedData) // Store full data for picker modal
            if (detectedArr.length > 1) {
              setShowStockPicker(true)
            }
          }

          // Auto-switch to detected asset type
          // Map API asset type to UI subcategory format
          const apiToUIMapping = {
            'bankAccounts': 'bank accounts',
            'mutualFunds': 'mutual funds',
            'fixedDeposits': 'fixed deposit',
            'homeProperty': 'property',
            'vehicles': 'vehicle',
            'crypto': 'crypto',
            'stocks': 'stocks',
            'insurance': 'insurance',
            'cash': 'cash',
            'businessOwnership': 'business ownership',
            'otherPhysicalAssets': 'other physical assets',
            'importantPapers': 'important papers',
            'preciousMetals': 'precious metals',
            'collectibles': 'collectibles',
            // Map category-based asset types to other physical assets
            'electronics': 'other physical assets',
            'furniture': 'other physical assets',
            'equipment': 'other physical assets',
            'appliance': 'other physical assets'
          };

          const uiSubCategory = apiToUIMapping[extractedAssetType] || extractedAssetType;
          const detectedAssetCategory = ['cash', 'property', 'vehicle', 'collectibles', 'precious metals', 'other physical assets'].includes(uiSubCategory) ? 'physical' : 'digital';

       
          // Only change asset type if it's different (to avoid resetting subcategory)
          if (currentAssetType !== detectedAssetCategory) {
            handleAssetTypeChange(detectedAssetCategory);
          }

          // Always set the correct subcategory
          handleSubCategoryChange(uiSubCategory);
          return null
        }

        // Asset type matches - proceed with auto-fill
        let extractedArr = getExtractedArrayForSubCategory(extractedData, currentSubCategory)

        // 🔥 FALLBACK — jab details key hi na ho (mutual fund case)
        if (!extractedArr || extractedArr.length === 0) {
          const normalized = normalizeExtractedItem(extractRes?.data, currentSubCategory)
          if (Object.keys(normalized).length > 0) {
            extractedArr = [normalized]
          }
        }

        // Set purchaseCurrency if present
        if (purchaseCurrency) {
          setFieldValue && setFieldValue("purchaseCurrency", purchaseCurrency)
        }

        if (extractedAssetType && normalize(extractedAssetType) === normalize(currentSubCategory)) {
          if (Array.isArray(extractedArr) && extractedArr.length > 0) {
            // ✅ SINGLE RECORD → AUTO FILL
            if (extractedArr.length === 1) {
              // Merge top-level data with detail object for all subcategories
              const mergedItem = { ...extractedData, ...extractedArr[0] }
              const warnings = fillExtractedFields(mergedItem, setFieldValue, currentSubCategory)

              // Show warnings for invalid dropdown values
              if (Object.keys(warnings).length > 0) {
                const warningMsg = Object.entries(warnings)
                  .map(([field, msg]) => `⚠️ ${field}: ${msg}`)
                  .join('\n');
                setExtractMessage(`✅ ${currentSubCategory} details auto-filled from document.\n\n${warningMsg}`)
              } else {
                setExtractMessage(`✅ ${currentSubCategory} details auto-filled from document.`)
              }
            }
            // ✅ MULTIPLE RECORD → POPUP (same as stocks)
            else {
              setExtractedItems(extractedArr)
              setFullExtractedData(extractedData) // Store full data for picker modal
              setShowStockPicker(true)
              setExtractMessage(`Multiple ${currentSubCategory} records found. Please select one.`)
            }
          }
        } else if (!extractedAssetType) {
          setExtractMessage("Could not detect asset type from document.")
        }
      } else {
        showErrorToast("Upload completed but no URL returned")
      }
      return s3Url
    } catch (err) {
      console.error("uploadDocumentAndExtract error", err)
      const apiMsg = err?.response?.data?.message || err?.message || "Failed to upload document for AI extraction"
      showErrorToast(apiMsg)
      setExtractMessage(apiMsg)
      // remove optimistic entry
      setUploadedDocuments((prev) => prev.filter((d) => d.id !== fileObj.id))
      return null
    } finally {
      setExtractLoading(false)
    }
  }, [currentSubCategory, currentAssetType, user, showSuccessToast, showErrorToast])

  // Document dropzone
  const [docInputKey, setDocInputKey] = useState(0);
  const docInputRef = useRef();
  const onDocumentDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.forEach((file) => {
        uploadDocumentAndExtract(file, setFieldValueRef.current)
      })
      // Reset the file input value to prevent dialog reopening
      if (docInputRef.current) {
        docInputRef.current.value = '';
      }
      setTimeout(() => setDocInputKey((k) => k + 1), 100);
    },
    [user, uploadDocumentAndExtract],
  )

  const {
    getRootProps: getDocRootProps,
    getInputProps: getDocInputProps,
    isDragActive: isDocDragActive,
    open: openDocInput,
  } = useDropzone({
    onDrop: onDocumentDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    maxSize: 10 * 1024 * 1024, // 10 MB
    multiple: true,
    noClick: true,
    noKeyboard: true,
  })

  const PICKER_CONFIG = {
    stocks: {
      title: "Select a Stock",
      name: (i) => `${i.stockName || ""} (${i.stockTicker || ""})`,
      meta: (i) =>
        `Shares: ${i.numberOfShares || "-"}, Avg Buy: ${i.averageBuyPrice || "-"}, Current: ${i.currentMarketPrice || "-"}, Value: ${i.estimatedValue || "-"}`,
    },

    crypto: {
      title: "Select a Crypto Asset",
      name: (i) => `${i.cryptoName || ""} (${i.cryptoTicker || ""})`,
      meta: (i) =>
        `Coins: ${i.numberOfCoins || "-"}, Avg Buy: ${i.averageBuyPrice || "-"}, Network: ${i.networkBlockchain || "-"}`,
    },

    "mutual funds": {
      title: "Select a Mutual Fund",
      name: (i) => i.name || i.planName || "",
      meta: (i) => `Units: ${i.units || "-"}, Invested: ${i.purchasePrice || "-"}, Value: ${i.estimatedValue || "-"}`,
    },

    "precious metals": {
      title: "Select a Precious Metal",
      name: (i) => `${i.name || i.metalType || ""} ${i.formType ? `(${i.formType})` : ""}`,
      meta: (i) =>
        `Metal: ${i.metalType || "-"}, Weight: ${i.weight || "-"} ${i.weightUnit || ""}, Purity: ${i.purity || "-"}, Value: ${i.estimatedCurrentValue || "-"}`,
    },
  }

  // Remove uploaded document
  const removeDocument = (id) => {
    setUploadedDocuments((prev) => prev.filter((doc) => doc.id !== id))
  }

  // Remove uploaded file
  const removeFile = (id) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== id))
  }

  // Add tag
  const addTag = () => {
    const trimmed = newTag.trim();
    // Tags are compared without case ("home" = "HOME"); clear the box so the next tag starts fresh (ML-06).
    const isDuplicate = tags.some((tag) => tag.toLowerCase() === trimmed.toLowerCase());
    if (
      trimmed.length < 1 ||
      trimmed.length > 30 ||
      isDuplicate ||
      tags.length >= 10
    ) {
      if (trimmed.length < 1) showErrorToast("Tag must be at least 1 character.");
      else if (trimmed.length > 30) showErrorToast("Tag must be at most 30 characters.");
      else if (isDuplicate) { showErrorToast("Duplicate tag not allowed."); setNewTag(""); }
      else if (tags.length >= 10) showErrorToast("Maximum 10 tags allowed.");
      return;
    }
    setTags((prev) => [...prev, trimmed]);
    setNewTag("");
  }

  // Remove tag
  const removeTag = (tagToRemove) => {
    setTags((prev) => prev.filter((tag) => tag !== tagToRemove))
  }

  // Handle tag input
  const handleTagInput = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      addTag()
    }
  }

  // Liability management functions
  const filteredLiabilities = liabilityOptions.filter(
    (option) =>
      option.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !liabilities.some((liability) => liability.name === option),
  )

  const addLiability = (name) => {
    const newLiability = {
      id: Date.now(),
      name: name,
    }
    setLiabilities((prev) => [...prev, newLiability])
    setSearchTerm("")
    setShowDropdown(false)
  }

  const deleteLiability = (id) => {
    setLiabilities((prev) => prev.filter((liability) => liability.id !== id))
  }

  // Auto-fill extracted data after form switch
  useEffect(() => {
    if (extractedItems && extractedItems.length > 0 && !showStockPicker && setFieldValueRef.current) {
      // If only one item, auto-fill immediately after a short delay
      if (extractedItems.length === 1) {
        const timer = setTimeout(() => {
          // Merge fullExtractedData with extractedItems[0] for complete data
          const mergedItem = { ...fullExtractedData, ...extractedItems[0] }
          const warnings = fillExtractedFields(mergedItem, setFieldValueRef.current, currentSubCategory)

          // Show warnings for invalid dropdown values
          if (Object.keys(warnings).length > 0) {
            const warningMsg = Object.entries(warnings)
              .map(([field, msg]) => `⚠️ ${field}: ${msg}`)
              .join('\n');
            setExtractMessage(`✅ ${currentSubCategory} details auto-filled from document.\n\n${warningMsg}`)
          } else {
            setExtractMessage(`✅ ${currentSubCategory} details auto-filled from document.`)
          }

          setExtractedItems([]) // Clear to avoid re-filling
        }, 300)
        return () => clearTimeout(timer)
      }
    }
  }, [extractedItems, showStockPicker, currentSubCategory])

  // Handle asset type change
  const handleAssetTypeChange = (assetType) => {
    // Don't reset if already on this asset type
    if (currentAssetType === assetType) {
      return;
    }

    const defaultSubCategory = assetType === "digital" ? "insurance" : "cash"
    setCurrentAssetType(assetType)
    setCurrentSubCategory(defaultSubCategory)
    setFormKey((prev) => prev + 1) // Force remount
  }

  // Handle subcategory change
  const handleSubCategoryChange = (subCategory) => {
    setCurrentSubCategory(subCategory)
    setFormKey((prev) => prev + 1) // Force remount
  }

  const handleAddReminder = () => {
    if (!reminderForm.description.trim()) {
      showErrorToast("Description is required for reminder")
      return
    }

    if (editingReminderId) {
      setReminders((prev) => prev.map((r) => (r.id === editingReminderId ? { ...reminderForm, id: r.id } : r)))
      setEditingReminderId(null)
      showSuccessToast("Reminder updated successfully")
    } else {
      const newReminder = {
        ...reminderForm,
        id: Date.now(),
      }
      setReminders((prev) => [...prev, newReminder])
      showSuccessToast("Reminder added successfully")
      setEditingReminderId('temp')
      setRemindersCollapsed(false)
      setTimeout(() => setEditingReminderId(null), 0)
    }

    // Reset form
    setReminderForm({
      description: "",
      type: "Monthly",
      dayOfMonth: "1",
      monthOfYear: "1",
    })
  }

  const handleEditReminder = (reminder) => {
    setReminderForm({
      description: reminder.description,
      type: reminder.type,
      dayOfMonth: reminder.dayOfMonth,
      monthOfYear: reminder.monthOfYear,
    })
    setEditingReminderId(reminder.id)
    // Expand list if editing
    setRemindersCollapsed(false)
  }

  const handleDeleteReminder = (id) => {
    setReminders((prev) => prev.filter((r) => r.id !== id))
    showSuccessToast("Reminder deleted")
  }

  const handleCompleteReminder = (id) => {
    // In add mode, just remove from local state
    setReminders((prev) => prev.filter((r) => r.id !== id))
    showSuccessToast("Reminder marked as complete")
  }

  const getMonthName = (monthNum) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ]
    return months[Number.parseInt(monthNum) - 1]
  }

  // Handle form submission
  const validAccountTypes = ["savings", "current", "fixed", "recurring"]
  const validOwnershipTypes = ["individual", "joint", "company"]

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!user || !user.id) {
      showErrorToast("You must be logged in to add an asset.")
      setSubmitting(false)
      return
    }
    // Map subCategory to details key and fields
    let detailsKey = ""
    let detailsFields = []
    switch (values.subCategory) {
      case "insurance":
        detailsKey = "insuranceDetails"
        detailsFields = [
          "providerName",
          "policyNumber",
          "maturityDate",
          "premiumAmount",
          "policyHolderName",
          "sumAssured",
          "policyStartDate",
          "premiumFrequency",
          "insuranceType",
          "policyName",
        ]
        break
      case "vehicle":
        detailsKey = "vehicleDetails"
        detailsFields = ["vehicleType", "vehicleName", "manufacturingYear", "registrationNumber", "owners"]
        break
      case "mutual funds":
        detailsKey = "mutualFundDetails"
        detailsFields = [
          "providerName",
          "planName",
          "platformProvider",
          "folioNumber",
          "units",
          "fundType",
          "currentEstimateValue",
        ]
        break
      case "cash":
        detailsKey = "cashDetails"
        detailsFields = ["cashLabel", "amount", "storageLocation"]
        break
      case "property":
        detailsKey = "propertyDetails"
        detailsFields = ["propertyName", "propertyType", "propertyAddress", "ownershipType", "ownershipPercentage"]
        break
      case "bank accounts":
        detailsKey = "bankAccountDetails"
        detailsFields = [
          "providerName",
          "accountNumber",
          "accountType",
          "balance",
          "balanceCurrency",
          "country",
          "ownershipType",
          "accountName",
        ]
        break
      case "fixed deposit":
        detailsKey = "fixedDepositDetails"
        detailsFields = [
          "providerName",
          "depositAmount",
          "depositLabel",
          "interestRate",
          "maturityDate",
          "accountNumber",
          "startDate",
          "currentEstimateValue",
          "maturityAmount",
          "ownershipType",
          "interestPayoutFrequency",
        ]
        break
      case "stocks":
        detailsKey = "stockDetails"
        detailsFields = [
          "stockName",
          "stockTicker",
          "brokerPlatform",
          "numberOfShares",
          "currentMarketPrice",
          "estimatedValue",
          "dividendPaying",
        ]
        break
      case "crypto":
        detailsKey = "cryptoDetails"
        detailsFields = [
          "cryptoName",
          "cryptoTicker",
          "walletExchange",
          "numberOfCoins",
          "totalOwned",
          "currentMarketPrice",
          "networkBlockchain",
          "walletAddress",
          "estimatedCurrentValue",
        ]
        break
      case "precious metals":
        detailsKey = "preciousMetalDetails"
        detailsFields = [
          "metalType",
          "formType",
          "weightQuantity",
          "weightUnit",
          "purity",
          "currentMarketPrice",
          "estimatedCurrentValue",
          "storageLocation",
          "numberOfCoins",
          "taxIncluded",
          "gstAmount",
          "merchantDetails",
          "paymentMethod",
          "invoiceNumber",
        ]
        // Only include ownershipType in detailsFields if it has a value
        if (values.ownershipType && values.ownershipType.trim().length > 0) {
          detailsFields.push("ownershipType");
        }
        break
      case "important papers":
        detailsKey = "importantPapersDetails"
        detailsFields = [
          "documentName",
          "category",
          "issuingAuthority",
          "documentNumber",
          "issueDate",
          "expiryDate",
          "linkedAsset",
          "documentStatus",
          "documentType",
          "ownershipType",
        ]
        break
      case "collectibles":
        detailsKey = "collectiblesDetails"
        detailsFields = ["categoryType", "description", "estimatedCurrentValue", "sourceSeller", "ownershipType"]
        break
      case "business ownership":
        detailsKey = "businessOwnershipDetails"
        detailsFields = [
          "ownershipType",
          "ownershipPercentage",
          "countryOfRegistration",
          "businessType",
          "equityType",
          "valuationSource",
          "estimatedCurrentValue",
        ]
        break
      case "other physical assets":
        detailsKey = "otherPhysicalAssetsDetails"
        detailsFields = ["category", "brand", "model", "serialNumber", "ownershipType", "estimatedCurrentValue"]
        break
      default:
        detailsKey = null
        detailsFields = []
    }
    //for Name
    let detailsObj = {}
    detailsFields.forEach((field) => {
      if (values.subCategory === "property" && field === "propertyName") {
        // Always set propertyName to values.name
        detailsObj["propertyName"] = values.name
      } else if (values.subCategory === "vehicle" && field === "vehicleName") {
        // Always set vehicleName to values.name
        detailsObj["vehicleName"] = values.name
      } else if (values.subCategory === "cash" && field === "cashLabel") {
        // Always set cashLabel to values.name
        detailsObj["cashLabel"] = values.name
      } else if (values.subCategory === "crypto" && field === "cryptoName") {
        detailsObj["cryptoName"] = values.name
      } else if (values.subCategory === "stocks" && field === "stockName") {
        detailsObj["stockName"] = values.name
      } else if (values.subCategory === "stocks" && field === "dividendPaying") {
        detailsObj["dividendPaying"] = values.dividendPaying === "yes"
      } else if (values.subCategory === "fixed deposit" && field === "providerName") {
        detailsObj["providerName"] = values.name
      } else if (values.subCategory === "mutual funds" && field === "planName") {
        detailsObj["planName"] = values.name
      } else if (values.subCategory === "bankAccounts" && field === "providerName") {
        detailsObj["providerName"] = values.name
      } else if (values.subCategory === "insurance" && field === "providerName") {
        // prefer explicit providerName, otherwise fallback to top-level name
        detailsObj["providerName"] = values.providerName || ""
      } else if (values.subCategory === "insurance" && field === "policyName") {
        // prefer explicit policyName, else fallback to top-level name
        detailsObj["policyName"] = values.policyName || ""
      } else if (values.subCategory === "insurance" && field === "sumAssured") {
        // prefer explicit policyName, else fallback to top-level name
        detailsObj["sumAssured"] = values.sumAssured || values.purchasePrice || ""
      } else if (values.subCategory === "important papers" && field === "documentName") {
        // Always set documentName to values.name for important papers
        detailsObj["documentName"] = values.name
      } else if (values[field] !== undefined) {
        detailsObj[field] = values[field]
      }
    })

    const estimatedValue = {}
    detailsFields.forEach((field) => {
      if (values.subCategory === "crypto" && field === "currentMarketPrice") {
        // Use correct currentMarketPrice from form values
        estimatedValue["currentMarketPrice"] = values.currentMarketPrice
      } else if (values.subCategory === "stocks" && field === "dividendPaying") {
        estimatedValue["dividendPaying"] = values.dividendPaying === "yes"
      } else if (values[field] !== undefined) {
        estimatedValue[field] = values[field]
      }
    })

    // For bank accounts, always build detailsObj from flat values and validate enums
    if (values.subCategory === "bank accounts") {
      detailsObj = {
        bankName: values.bankName || "",
        accountName: values.accountName || "",
        accountType: values.accountType || "",
        accountNumber: values.accountNumber || "",
        currentBalance: values.currentBalance || "",
        currentBalanceCurrency: values.currentBalanceCurrency || "",
        country: values.country || "",
        bankLocation: values.bankLocation || "",
        ownershipType: values.ownershipType || "",
        providerName: values.providerName || "",
      }
    }

    // For fixed deposits, explicitly include ownershipType and interestPayoutFrequency
    if (values.subCategory === "fixed deposit") {
      detailsObj.ownershipType = values.ownershipType || ""
      detailsObj.interestPayoutFrequency = values.interestPayoutFrequency || ""
    }

    // Upload all files before creating asset and collect their URLs and metadata
    const uploadedFileInfos = []
    for (const fileObj of uploadedFiles) {
      let s3Response = null
      let fileUrl = fileObj.uploadedUrl
      let fileName = fileObj.name
      if (!fileObj.uploadedUrl) {
        s3Response = await uploadToS3(fileObj.file, {
          user,
          setImageUploading: setFileUploading,
          setProgresses: setFileProgresses,
          showSuccessToast,
          showErrorToast,
          setErrorMessages: setFileErrorMessages,
          updateReduxUser: false,
        })
        if (s3Response && typeof s3Response === "object") {
          fileUrl = s3Response.url || s3Response.fileName
          fileName = s3Response.fileName || fileObj.name
        } else if (typeof s3Response === "string") {
          fileUrl = s3Response
        }
      }
      uploadedFileInfos.push({
        url: fileUrl,
        type: fileObj.type,
        fileCategory: fileObj.type && fileObj.type.startsWith("image/") ? "other" : "other",
        size: fileObj.size,
        mimetype: fileObj.type,
        originalName: fileObj.name,
        filename: fileName,
      })
    }

    // Separate uploaded file URLs by type
    const photos = []
    const documents = []
    uploadedFileInfos.forEach((f) => {
      if (f.url) {
        if (f.type && f.type.startsWith("image/")) {
          photos.push(f)
        } else if (
          f.type === "application/pdf" ||
          f.type === "application/msword" ||
          f.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ) {
          documents.push(f)
        }
      }
    })

    // Map subCategory to correct API assetType value if needed
    let assetTypeApiValue = values.subCategory
    if (values.subCategory === "bank accounts") assetTypeApiValue = "bankAccounts"
    else if (values.subCategory === "mutual funds") assetTypeApiValue = "mutualFunds"
    else if (values.subCategory === "fixed deposit") assetTypeApiValue = "fixedDeposits"
    else if (values.subCategory === "property") assetTypeApiValue = "homeProperty"
    else if (values.subCategory === "vehicle") assetTypeApiValue = "vehicles"
    else if (values.subCategory === "business ownership") assetTypeApiValue = "businessOwnership"
    else if (values.subCategory === "Property") assetTypeApiValue = "homeProperty"
    else if (values.subCategory === "Vehicle") assetTypeApiValue = "vehicles"
    else if (values.subCategory === "vehicle") assetTypeApiValue = "vehicles"
    else if (values.subCategory === "other physical assets") assetTypeApiValue = "otherPhysicalAssets"
    else if (values.subCategory === "precious metals") assetTypeApiValue = "preciousMetals"
    else if (values.subCategory === "collectibles") assetTypeApiValue = "collectibles"
    else if (values.subCategory === "important papers") assetTypeApiValue = "importantPapers"

    const payload = {
      userId: (user && user.id) || "",
      name: values.name || "",
      purchaseDate: values.purchaseDate || "",
      purchasePrice: values.purchasePrice || "",
      purchaseCurrency: values.purchaseCurrency || "",
      // Set isDraft to false when saving as a regular asset (not draft)
      isDraft: false,
      // Map currentEstimateValue to estimatedValue if present
      estimatedValue:
        values.currentEstimateValue !== undefined &&
        values.currentEstimateValue !== null &&
        values.currentEstimateValue !== ""
          ? values.currentEstimateValue
          : values.estimatedValue ||
            (values.subCategory === "crypto" ? values.currentMarketPrice : values.purchasePrice),
      assetCategory: values.assetType,  // This maps to digital/physical from asset type toggle
      assetType: assetTypeApiValue,
      isActive: values.isActive === "yes" ? true : false,
      tags: tags,
      photos,
      documents,
      notes: values.notes || "",
      description: values.description || values.notes || "",
      linkedLiability: values.linkedLiability || [],
    }

    if (detailsKey && Object.keys(detailsObj).length > 0) {
      // include businessName derived from top-level name for business ownership
      if (detailsKey === "businessOwnershipDetails") {
        detailsObj.businessName = values.name || ""
        detailsObj.investmentAmount = values.purchasePrice || ""
        detailsObj.dateOfInvestment = values.purchaseDate || ""
      }
      // include other physical assets specifics
      if (detailsKey === "otherPhysicalAssetsDetails") {
        // Prefer explicit subform fields, fallback to sensible values
        detailsObj.category = values.category || values.otherCategory || ""
        // brandModel form field may contain both brand and model; map sensibly
        if (values.brandModel) {
          // set brand to first token and model to full string as a best-effort mapping
          const parts = values.brandModel.split(/\s+/)
          detailsObj.brand = values.brand || parts[0] || values.brandModel || ""
          detailsObj.model = values.model || values.brandModel || ""
        } else {
          detailsObj.brand = values.brand || ""
          detailsObj.model = values.model || ""
        }
        detailsObj.serialNumber = values.serialNumber || values.serial || ""
        detailsObj.ownershipType = values.ownershipType || ""
        // include estimatedCurrentValue if provided in subform
        if (values.estimatedCurrentValue !== undefined) {
          detailsObj.estimatedCurrentValue = values.estimatedCurrentValue
        }
      }
      // include precious metals specifics
      if (detailsKey === "preciousMetalDetails") {
        detailsObj.metalType = values.metalType || values.metal || ""
        // map formType -> form
        detailsObj.form = values.formType || values.form || ""
        detailsObj.weight = values.weightQuantity || values.weight || ""
        detailsObj.weightUnit = values.weightUnit || "g"
        detailsObj.purity = values.purity || ""
        detailsObj.currentMarketPricePerGram = values.currentMarketPrice || values.currentMarketPricePerGram || ""
        detailsObj.storageLocation = values.storageLocation || ""
        // ownershipType removed as per requirement - only include if it has a value
        if (values.ownershipType && values.ownershipType.trim().length > 0) {
          detailsObj.ownershipType = values.ownershipType;
        }
        detailsObj.numberOfCoins = values.numberOfCoins || ""
        detailsObj.taxIncluded = values.taxIncluded || false
        detailsObj.gstAmount = values.gstAmount || 0
        detailsObj.merchantDetails = values.merchantDetails || ""
        detailsObj.paymentMethod = values.paymentMethod || ""
        detailsObj.invoiceNumber = values.invoiceNumber || ""
        if (values.estimatedCurrentValue !== undefined) {
          detailsObj.estimatedCurrentValue = values.estimatedCurrentValue
        }
      }
      // include collectibles specifics
      if (detailsKey === "collectiblesDetails") {
        detailsObj.category = values.categoryType || values.category || ""
        detailsObj.description = values.description || ""
        detailsObj.sourceSeller = values.sourceSeller || ""
        detailsObj.ownershipType = values.ownershipType || ""
        if (values.estimatedCurrentValue !== undefined) {
          detailsObj.estimatedCurrentValue = values.estimatedCurrentValue
        }
      }
      // include important papers specifics
      if (detailsKey === "importantPapersDetails") {
        detailsObj.documentType = values.documentType || values.type || values.name || ""
        detailsObj.category = values.category || values.documentCategory || ""
        // issuingAuthority / issuer alias
        detailsObj.issuingAuthority = values.issuingAuthority || values.issuer || ""
        detailsObj.documentNumber = values.documentNumber || values.referenceNumber || ""
        detailsObj.issueDate = values.issueDate || ""
        detailsObj.expiryDate = values.expiryDate || ""
        detailsObj.ownershipType = values.ownershipType || ""
        detailsObj.storageLocation = values.storageLocation || ""
        detailsObj.isDigital = typeof values.isDigital === "boolean" ? values.isDigital : values.isDigital || false
        if (values.estimatedCurrentValue !== undefined) {
          detailsObj.estimatedCurrentValue = values.estimatedCurrentValue
        }
      }
      // include rental income for property
      if (detailsKey === "propertyDetails") {
        if (values.rentalIncome) {
          detailsObj.rentalIncome = {
            amount: values.rentalIncome.amount || 0,
            frequency: values.rentalIncome.frequency || "monthly",
          }
        }
      }
      // Leave out optional fields the user left empty: the API rejects "" for enum fields such as
      // ownershipType or interestPayoutFrequency, which made the Fixed Deposit save fail (ML-32).
      Object.keys(detailsObj).forEach((key) => {
        if (detailsObj[key] === "") delete detailsObj[key]
      })
      payload[detailsKey] = detailsObj
    }

    // If businessOwnershipDetails provided an estimatedCurrentValue, use it as payload.estimatedValue
    if (payload.businessOwnershipDetails && payload.businessOwnershipDetails.estimatedCurrentValue) {
      payload.estimatedValue = payload.businessOwnershipDetails.estimatedCurrentValue
    }

    // If cryptoDetails provided an estimatedCurrentValue, use it as payload.estimatedValue
    if (payload.cryptoDetails && payload.cryptoDetails.estimatedCurrentValue) {
      payload.estimatedValue = payload.cryptoDetails.estimatedCurrentValue
    }

    // If otherPhysicalAssetsDetails provided an estimatedCurrentValue, use it as payload.estimatedValue
    if (payload.otherPhysicalAssetsDetails && payload.otherPhysicalAssetsDetails.estimatedCurrentValue) {
      payload.estimatedValue = payload.otherPhysicalAssetsDetails.estimatedCurrentValue
    }

    // If preciousMetalDetails provided an estimatedCurrentValue, use it as payload.estimatedValue
    if (payload.preciousMetalDetails && payload.preciousMetalDetails.estimatedCurrentValue) {
      payload.estimatedValue = payload.preciousMetalDetails.estimatedCurrentValue
    }

    // If collectiblesDetails provided an estimatedCurrentValue, use it as payload.estimatedValue
    if (payload.collectiblesDetails && payload.collectiblesDetails.estimatedCurrentValue) {
      payload.estimatedValue = payload.collectiblesDetails.estimatedCurrentValue
    }

    // If importantPapersDetails provided an estimatedCurrentValue, use it as payload.estimatedValue
    if (payload.importantPapersDetails && payload.importantPapersDetails.estimatedCurrentValue) {
      payload.estimatedValue = payload.importantPapersDetails.estimatedCurrentValue
    }

    // For important papers, UI hides purchase fields — provide safe defaults if missing
    if (values.subCategory === "important papers") {
      const today = new Date().toISOString().slice(0, 10)
      payload.purchaseDate = payload.purchaseDate || today
      payload.purchasePrice = payload.purchasePrice || 0
      payload.purchaseCurrency = payload.purchaseCurrency || selectedCurrency || "USD"
      payload.estimatedValue = payload.estimatedValue || 0
    }

    try {
      const result = await dispatch(createAsset(payload))
      if (createAsset.fulfilled.match(result)) {
        setIsSaved(true)
        const assetId = result?.payload?.data?._id || result?.payload?._id

        // Link multiple liabilities if selected (make one PATCH call per liability)
        if (values.linkedLiability && values.linkedLiability.length > 0 && assetId) {
          try {
            const liabilityIds = Array.isArray(values.linkedLiability) ? values.linkedLiability : [values.linkedLiability];
            for (const liabilityId of liabilityIds) {
              await api.patch(`/liabilities/${liabilityId}`, { assetId }, { headers: getAuthHeaders() });
            }
          } catch (liabilityError) {
            console.error('Error linking liability:', liabilityError)
            // Continue with flow even if liability linking fails
          }
        }

        // Create reminders if setReminder is yes
        if (values.setReminder === "yes") {
          const remindersToCreate = reminders.length > 0 ? reminders : [reminderForm];

          for (const reminder of remindersToCreate) {
            try {
              const reminderPayload = {
                userId: user.id,
                assetId: assetId,
                referenceType: "asset",
                type: "other",
                date: new Date().toISOString(),
                description: reminder.description,
                recurrencePattern: {}
              }

              // Build recurrence pattern based on reminder type
              if (reminder.type === "Weekly") {
                reminderPayload.recurrencePattern = {
                  type: "weekly"
                }
              } else if (reminder.type === "Monthly") {
                reminderPayload.recurrencePattern = {
                  type: "monthly",
                  dayOfMonth: Number.parseInt(reminder.dayOfMonth)
                }
              } else if (reminder.type === "Yearly") {
                reminderPayload.recurrencePattern = {
                  type: "yearly",
                  month: Number.parseInt(reminder.monthOfYear),
                  day: Number.parseInt(reminder.dayOfMonth)
                }
              }

              await createReminder(reminderPayload)
            } catch (reminderError) {
              console.error("Error creating reminder:", reminderError)
              // Don't fail the asset creation if reminder fails
              showErrorToast("Asset created but failed to set reminder")
            }
          }
        }

        showSuccessToast("Asset added successfully!")
        resetForm()
        setUploadedFiles([])
        setUploadedDocuments([])
        setTags([])
        setNewTag("")
        setReminders([])
        setShowReminderSection("no")
        setFormKey((prev) => prev + 1) // Reset form key
        navigate("/my-assets")
      } else {
        const apiMsg = result?.payload?.message || "Failed to add asset. Please try again."
        showErrorToast(apiMsg)
      }
    } catch (error) {
      // Try to show API error message if available
      const apiMsg = error?.response?.data?.message || error?.message || "Failed to add asset. Please try again."
      showErrorToast(apiMsg)
    } finally {
      setSubmitting(false)
    }
  }

  // Handle save as draft
  // Save as Draft: only require 'name', skip all other validations
  // This function should be called directly from the button, not as a Formik submit handler
  const handleSaveAsDraft = async (values) => {
  // No Formik helpers, so use safe fallbacks
  const setSubmitting = () => {};
  const resetForm = () => {};
    if (!values.name || values.name.trim() === "") {
      showErrorToast("Name is required to save as draft.");
      return;
    }
    // Map subCategory to details key and fields (reuse logic from handleSubmit)
    let detailsKey = "";
    let detailsFields = [];
    switch (values.subCategory) {
      case "insurance":
        detailsKey = "insuranceDetails";
        detailsFields = ["providerName", "policyNumber", "maturityDate", "premiumAmount", "policyHolderName", "sumAssured", "policyStartDate", "premiumFrequency", "insuranceType", "policyName"];
        break;
      case "vehicle":
        detailsKey = "vehicleDetails";
        detailsFields = ["vehicleType", "vehicleName", "manufacturingYear", "registrationNumber", "owners"];
        break;
      case "mutual funds":
        detailsKey = "mutualFundDetails";
        detailsFields = ["providerName", "planName", "platformProvider", "folioNumber", "units", "fundType", "currentEstimateValue"];
        break;
      case "cash":
        detailsKey = "cashDetails";
        detailsFields = ["cashLabel", "amount", "storageLocation"];
        break;
      case "property":
        detailsKey = "propertyDetails";
        detailsFields = ["propertyName", "propertyType", "propertyAddress", "ownershipType", "ownershipPercentage"];
        break;
      case "bank accounts":
        detailsKey = "bankAccountDetails";
        detailsFields = ["providerName", "accountNumber", "accountType", "balance", "balanceCurrency", "country", "ownershipType", "accountName"];
        break;
      case "fixed deposit":
        detailsKey = "fixedDepositDetails";
        detailsFields = ["providerName", "depositAmount", "depositLabel", "interestRate", "maturityDate", "accountNumber", "startDate", "currentEstimateValue", "maturityAmount", "ownershipType", "interestPayoutFrequency"];
        break;
      case "stocks":
        detailsKey = "stockDetails";
        detailsFields = ["stockName", "stockTicker", "brokerPlatform", "numberOfShares", "currentMarketPrice", "estimatedValue", "dividendPaying"];
        break;
      case "crypto":
        detailsKey = "cryptoDetails";
        detailsFields = ["cryptoName", "cryptoTicker", "walletExchange", "numberOfCoins", "totalOwned", "currentMarketPrice", "networkBlockchain", "walletAddress"];
        break;
      case "precious metals":
        detailsKey = "preciousMetalDetails";
        detailsFields = ["metalType", "formType", "weightQuantity", "weightUnit", "purity", "currentMarketPrice", "estimatedCurrentValue", "storageLocation", "numberOfCoins", "taxIncluded", "gstAmount", "merchantDetails", "paymentMethod", "invoiceNumber"];
        break;
      case "important papers":
        detailsKey = "importantPapersDetails";
        detailsFields = ["documentType", "issuer", "issueDate", "expiryDate", "referenceNumber", "estimatedCurrentValue"];
        break;
      case "collectibles":
        detailsKey = "collectiblesDetails";
        detailsFields = ["categoryType", "description", "estimatedCurrentValue", "sourceSeller", "ownershipType"];
        break;
      case "business ownership":
        detailsKey = "businessOwnershipDetails";
        detailsFields = ["ownershipType", "ownershipPercentage", "countryOfRegistration", "businessType", "equityType", "valuationSource", "estimatedCurrentValue"];
        break;
      case "other physical assets":
        detailsKey = "otherPhysicalAssetsDetails";
        detailsFields = ["category", "brand", "model", "serialNumber", "ownershipType", "estimatedCurrentValue"];
        break;
      default:
        detailsKey = null;
        detailsFields = [];
    }

    // Build detailsObj for the detailsKey
    let detailsObj = {};
    detailsFields.forEach((field) => {
      if (values.subCategory === "property" && field === "propertyName") {
        detailsObj["propertyName"] = values.name;
      } else if (values.subCategory === "vehicle" && field === "vehicleName") {
        detailsObj["vehicleName"] = values.name;
      } else if (values.subCategory === "cash" && field === "cashLabel") {
        detailsObj["cashLabel"] = values.name;
      } else if (values.subCategory === "crypto" && field === "cryptoName") {
        detailsObj["cryptoName"] = values.name;
      } else if (values.subCategory === "stocks" && field === "stockName") {
        detailsObj["stockName"] = values.name;
      } else if (values.subCategory === "stocks" && field === "dividendPaying") {
        detailsObj["dividendPaying"] = values.dividendPaying === "yes";
      } else if (values.subCategory === "fixed deposit" && field === "providerName") {
        detailsObj["providerName"] = values.name;
      } else if (values.subCategory === "mutual funds" && field === "planName") {
        detailsObj["planName"] = values.name;
      } else if (values.subCategory === "bank accounts" && field === "providerName") {
        detailsObj["providerName"] = values.name;
      } else if (values.subCategory === "insurance" && field === "providerName") {
        detailsObj["providerName"] = values.providerName || "";
      } else {
        detailsObj[field] = values[field] || "";
      }
    });

    // For fixed deposits in draft mode, explicitly include ownershipType and interestPayoutFrequency
    if (values.subCategory === "fixed deposit") {
      detailsObj.ownershipType = values.ownershipType || ""
      detailsObj.interestPayoutFrequency = values.interestPayoutFrequency || ""
    }

    // Map subCategory to correct API assetType value if needed 
    //bank account changes for draft vk
    let assetTypeApiValue = values.subCategory
    if (values.subCategory === "bank accounts") assetTypeApiValue = "bankAccounts"
    else if (values.subCategory === "mutual funds") assetTypeApiValue = "mutualFunds"
    else if (values.subCategory === "fixed deposit") assetTypeApiValue = "fixedDeposits"
    else if (values.subCategory === "property") assetTypeApiValue = "homeProperty"
    else if (values.subCategory === "vehicle") assetTypeApiValue = "vehicles"
    else if (values.subCategory === "business ownership") assetTypeApiValue = "businessOwnership"
    else if (values.subCategory === "other physical assets") assetTypeApiValue = "otherPhysicalAssets"
    else if (values.subCategory === "precious metals") assetTypeApiValue = "preciousMetals"
    else if (values.subCategory === "collectibles") assetTypeApiValue = "collectibles"
    else if (values.subCategory === "important papers") assetTypeApiValue = "importantPapers"

    // Build the draft payload
    // Only include fields with non-empty values
    let draftPayload = { isDraft: true };
    const baseFields = {
      userId: (user && user.id) || "",
      name: values.name,
      purchaseDate: values.purchaseDate,
      purchasePrice: values.purchasePrice,
      purchaseCurrency: values.purchaseCurrency,
      estimatedValue: values.currentEstimateValue !== undefined && values.currentEstimateValue !== null && values.currentEstimateValue !== ""
        ? values.currentEstimateValue
        : (values.estimatedValue || (values.subCategory === "crypto" ? values.currentMarketPrice : values.purchasePrice)),
      assetCategory: values.assetType,  // This maps to digital/physical from asset type toggle
      assetType: assetTypeApiValue,
      isActive: values.isActive === "yes" ? true : false,
      tags: tags,
      photos: [],
      documents: [],
      notes: values.notes,
      description: values.description || values.notes,
    };
    Object.entries(baseFields).forEach(([key, val]) => {
      if (val !== undefined && val !== null && val !== "" && !(Array.isArray(val) && val.length === 0)) {
        draftPayload[key] = val;
      }
    });
    if (detailsKey && Object.keys(detailsObj).length > 0) {
      // Remove empty fields from detailsObj
      const cleanedDetailsObj = {};
      Object.entries(detailsObj).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== "" && !(Array.isArray(val) && val.length === 0)) {
          cleanedDetailsObj[key] = val;
        }
      });
      if (Object.keys(cleanedDetailsObj).length > 0) {
        draftPayload[detailsKey] = cleanedDetailsObj;
      }
    }
    try {
      const result = await dispatch(createAsset(draftPayload));
      if (createAsset.fulfilled && createAsset.fulfilled.match(result)) {
        showSuccessToast("Draft saved successfully!");
        resetForm();
        setUploadedFiles([]);
        setUploadedDocuments([]);
        setTags([]);
        setNewTag("");
        setFormKey(prev => prev + 1); // Reset form key
        navigate("/my-assets")
      } else {
        let apiMsg = result?.payload?.message || "Failed to save draft.";
        showErrorToast(apiMsg);
      }
    } catch (error) {
      const apiMsg = error?.response?.data?.message || error?.message || "Failed to save draft."
      showErrorToast(apiMsg)
    }
  }

  // Step navigation
  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  // Validate current step
  const validateStep = (values, errors) => {
    switch (currentStep) {
      case 1:
        return !errors.assetType && !errors.subCategory
      case 2:
        return true
      case 3:
        return !errors.setReminder && !errors.notes && !errors.isActive
      default:
        return true
    }
  }

  // Render subcategory form based on selection
  const renderSubCategoryForm = (values, setFieldValue, errors, touched, defaultCurrency) => {
    switch (values.subCategory) {
      case "insurance":
        return (
          <InsuranceForm
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            touched={touched}
            defaultCurrency={defaultCurrency}
          />
        )
      case "vehicle":
        return <VehicleForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />
      case "cash":
        return <CashForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />
      case "property":
        return <PropertyForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />
      case "bank accounts":
        return <BankAccountsForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />
      case "mutual funds":
        return <MutualFundsForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />
      case "fixed deposit":
        return <FixedDepositForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />
      case "stocks":
        return <StocksForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />
      case "crypto":
        return <CryptoForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />
      case "collectibles":
        return <CollectiblesForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />
      case "business ownership":
        return <BusinessOwnershipForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />
      case "other physical assets":
        return (
          <OtherPhysicalAssetsForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />
        )
      case "important papers":
        return <ImportantPapersForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />
      case "precious metals":
        return <PreciousMetalsForm values={values} setFieldValue={setFieldValue} errors={errors} touched={touched} />

      default:
        return <div className="text-muted">Please select a sub category to continue</div>
    }
  }

  // Render step content
  const renderStepContent = (values, setFieldValue, errors, touched, resetForm) => {
    // Keep setFieldValue ref updated for async use
    setFieldValueRef.current = setFieldValue

    // Stock picker modal
    const handleStockSelect = (item) => {
      fillExtractedFields(item, setFieldValue, currentSubCategory)
      setShowStockPicker(false)
      setExtractedItems([])
      setExtractMessage(
        `${currentSubCategory.charAt(0).toUpperCase() + currentSubCategory.slice(1)} details filled from selected item.`,
      )
    }

    const PickerModal = () => {
      const cfg = PICKER_CONFIG[currentSubCategory]
      if (!cfg) return null

      return (
        <Modal show={showStockPicker} onHide={() => setShowStockPicker(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{cfg.title}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {extractedItems.map((item, idx) => (
              <div key={idx} className="mb-3 p-2 border rounded">
                <div>
                  <b>{cfg.name(item)}</b>
                </div>
                <div className="text-muted small">{cfg.meta(item)}</div>

                <Button
                  variant="primary"
                  className="mt-2"
                  onClick={() => {
                    // Merge full extracted data with selected item for proper field mapping
                    const mergedItem = fullExtractedData ? { ...fullExtractedData, ...item } : item
                    fillExtractedFields(mergedItem, setFieldValueRef.current, currentSubCategory)
                    setShowStockPicker(false)
                    setExtractedItems([])
                    setFullExtractedData(null)
                  }}
                >
                  Select
                </Button>
              </div>
            ))}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowStockPicker(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      )
    }

    if (!isMobile) {
      return (
        <>
          <PickerModal />
          <div className="row">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4>Add Assets</h4>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12 col-md-12">
              {renderStep1Content(values, setFieldValue, errors, touched, resetForm)}
            </div>
            <div className="col-lg-8 col-md-12">{renderStep2Content(values, setFieldValue, errors, touched)}</div>
            <div className="col-lg-4 col-md-12 ms-auto right-col">
              {renderStep3Content(values, setFieldValue, errors, touched)}
            </div>
          </div>
        </>
      )
    }

    // Mobile view - show step by step
    return (
      <>
        <PickerModal />
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4>Add Assets</h4>
        </div>
        {(() => {
          switch (currentStep) {
            case 1:
              return renderStep1Content(values, setFieldValue, errors, touched, resetForm)
            case 2:
              return renderStep2Content(values, setFieldValue, errors, touched)
            case 3:
              return renderStep3Content(values, setFieldValue, errors, touched)
            default:
              return null
          }
        })()}
      </>
    )
  }

  // Step 1: Document Upload & Asset Type
  const renderStep1Content = (values, setFieldValue, errors, touched, resetForm) => (
    <>
      <Formik>
        <form>
          {/* Premium Upload Area with drag and drop - WRAPPER */}
          {extractLoading && <Loader />}
          {extractMessage && <div className="alert alert-warning mt-2 text-danger">{extractMessage}</div>}
          <div
            {...getDocRootProps()}
            className={`upload-document-area ${fileUploading ? "uploading" : ""} ${isDocDragActive ? "drag-active" : ""}`}
          >
            {/* Floating particles */}
            <div className="particles">
              <div className="particle p1" />
              <div className="particle p2" />
              <div className="particle p3" />
              <div className="particle p4" />
            </div>
            {/* Sparkle decorations */}
            <div className="upload-sparkles">
              <svg className="upload-sparkle s1" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor" />
              </svg>
              <svg className="upload-sparkle s2" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor" />
              </svg>
              <svg className="upload-sparkle s3" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor" />
              </svg>
            </div>

            <Form.Label
              className="w-100 h-100 d-flex flex-column justify-content-center align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => openDocInput()}
            >
              {/* Animated icon */}
              <div className="d-flex justify-content-center align-items-center gap-3">
                <div>
                  <div className="icon-container">
                    <div className="icon-glow" />
                    <div className="icon-bg" />
                    <svg className="upload-sign" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 16V4M12 4L8 8M12 4L16 8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M20 16V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V16"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                </div>
                <div className="d-flex flex-col">
                  <span className="fw-semibold">
                    {isDocDragActive ? "Drop your document here" : "Upload your document"}
                  </span>
                  <small className="text-muted">
                    Drag & drop or <span style={{ color: "#3b82f6", fontWeight: 500 }}>click to browse</span>
                  </small>
                  <small className="text-muted mt-2" style={{ fontSize: "11px" }}>
                    PDF, PNG, JPG, DOCX • Max 10MB
                  </small>
                </div>
              </div>
              {/* AI Badge */}
              <div className="ai-badge-effect">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="white" />
                </svg>
                <span>AI powered extraction</span>
              </div>
            </Form.Label>

            <input
              key={docInputKey}
              ref={docInputRef}
              {...getDocInputProps()}
              id="ai-doc-upload-step1"
              onClick={e => {
                // Always reset value before opening dialog
                e.target.value = '';
                if (getDocInputProps().onClick) getDocInputProps().onClick(e);
              }}
            />
          </div>

          {/* Display uploaded documents if any */}
          {uploadedDocuments.length > 0 && (
            <div className="mb-3 flex">
              {uploadedDocuments.map((doc, index) => (
                <div key={doc.id} className="uploaded-file-item">
                  <div className="file-preview">
                    <i className="bi bi-file-earmark-pdf"></i>
                  </div>
                  <div className="file-info">
                    <span className="file-number">{index + 1}. </span>
                    <span className="file-name">{doc.name}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger remove-file"
                    onClick={() => removeDocument(doc.id)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="upload-divider-container">
            <hr />
            <span className="text-muted">OR ENTER MANUALLY</span>
          </div>
          {/* On mobile the manual fields are on the next step, so say so instead of showing an empty section (ML-40) */}
          {isMobile && (
            <p className="text-muted text-center small mt-2 mb-0">Tap Next to enter the asset details yourself.</p>
          )}
        </form>
      </Formik>
    </>
  )

  // Step 2: Dynamic Asset Details based on subcategory
  const renderStep2Content = (values, setFieldValue, errors, touched) => (
    <>
      <div className="row mb-4">
        {/* Asset Type Toggle */}
        <div className="col-12 mb-6">
          <label className="form-label">Asset Type</label>
          <div className="asset-type-toggle">
            <button
              type="button"
              className={`btn asset-type-btn ${currentAssetType === "digital" ? "active" : ""}`}
              onClick={() => handleAssetTypeChange("digital")}
            >
              Digital Assets
            </button>
            <button
              type="button"
              className={`btn asset-type-btn ${currentAssetType === "physical" ? "active" : ""}`}
              onClick={() => handleAssetTypeChange("physical")}
            >
              Physical Assets
            </button>
          </div>
          <ErrorMessage name="assetType" component="div" className="text-danger small" />
        </div>

        {/* Sub Category */}
        <div className="col-12">
          <label className="form-label">
            Sub Category<span className="text-danger">*</span>
          </label>
          <Select
            options={subCategoryOptionsByAssetType[values.assetType] || []}
            value={
              (subCategoryOptionsByAssetType[values.assetType] || []).find(
                (option) => option.value === values.subCategory,
              ) || null
            }
            onChange={(option) => handleSubCategoryChange(option.value)}
            className="react-select-container"
            classNamePrefix="react-select"
          />
          <ErrorMessage name="subCategory" component="div" className="text-danger small" />
        </div>
      </div>

      {/* Render all fields including name/date/price from GenericAssetForm config */}
      {renderSubCategoryForm(values, setFieldValue, errors, touched)}
    </>
  )

  // Step 3: Additional Details
  const renderStep3Content = (values, setFieldValue, errors, touched) => {
    // Get placeholders from field config
    const config = getFieldConfig(values.subCategory || currentSubCategory)
    const notesPlaceholder = config?.ui?.notesPlaceholder || "E.g., Purchase details, specific features, account recovery info..."
    const tagsPlaceholder = config?.ui?.tagsPlaceholder || "Add tag and press enter or click add button"

    return (
    <>
      <RightSidebar
        mode="add"
        readOnly={false}
        values={values}
        setFieldValue={setFieldValue}
        errors={errors}
        touched={touched}
        tags={tags}
        setTags={setTags}
        newTag={newTag}
        setNewTag={setNewTag}
        addTag={addTag}
        removeTag={removeTag}
        handleTagInput={handleTagInput}
        uploadedFiles={uploadedFiles}
        setUploadedFiles={setUploadedFiles}
        removeFile={removeFile}
        uploadedDocuments={[]}
        showReminder={true}
        subCategory={values.subCategory}
        notesPlaceholder={notesPlaceholder}
        tagsPlaceholder={tagsPlaceholder}
        linkedLiability={values.linkedLiability}
        reminderProps={{
          children: (
            <SetReminderSection
              showReminderSection={showReminderSection}
              setShowReminderSection={setShowReminderSection}
              setFieldValue={setFieldValue}
              values={values}
              reminderForm={reminderForm}
              setReminderForm={setReminderForm}
              reminders={reminders}
              remindersCollapsed={remindersCollapsed}
              setRemindersCollapsed={setRemindersCollapsed}
              editingReminderId={editingReminderId}
              handleAddReminder={handleAddReminder}
              handleEditReminder={handleEditReminder}
              handleDeleteReminder={handleDeleteReminder}
              handleCompleteReminder={handleCompleteReminder}
              getMonthName={getMonthName}
            />
          )
        }}
      />
    </>
    )
  }

  // This line is no longer needed as we use getCurrentFormInitialValues() directly in Formik

  return (
    <Layout>
      <div className="form-page-container">
        <div className="inner-container">
              {/* Step Progress Indicator for Mobile */}
              {isMobile && (
                <div className="step-progress mb-4 mt-2">
                  <div className="step-progress-bar">
                    <div className="step-progress-line">
                      <div className="step-progress-fill" style={{ width: `${(currentStep / 3) * 100}%` }}></div>
                    </div>
                    <div className="step-indicators">
                      {[1, 2, 3].map((step) => (
                        <div key={step} className={`step-indicator ${currentStep >= step ? "active" : ""}`}>
                          {step}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="step-title">
                    Step {currentStep} of 3:{" "}
                    {currentStep === 1
                      ? "Basic Information"
                      : currentStep === 2
                        ? "Asset Details"
                        : "Additional Details"}
                  </div>
                </div>
              )}

              {/* Only render Formik when we have initial values */}
              {selectedCurrency && (
                <Formik
                  key={formKey} // Force remount on key change
                  initialValues={getCurrentFormInitialValues()}
                  enableReinitialize={true} // Enable reinitialize for currency changes
                  validationSchema={getMergedValidationSchema(currentSubCategory)}
                  onSubmit={handleSubmit}
                  validateOnChange={true}
                  validateOnBlur={true}
                >
                  {({ values, setFieldValue, errors, touched, isSubmitting, resetForm, validateForm }) => {
                    // Get current validation schema for this render
                    const currentValidationSchema = getMergedValidationSchema(values.subCategory)

                    return (
                      <FormikForm>
                        {renderStepContent(values, setFieldValue, errors, touched, resetForm, selectedCurrency)}

                        {/* Action Buttons */}
                        <div className="action-buttons">
                          {isMobile ? (
                            // Mobile step navigation
                            <>
                              {(currentStep > 1 || currentStep === 1) && (
                                <div className="mobile-nav-buttons">
                                  <button type="button" className="btn btn-outline-secondary" onClick={currentStep > 1 ? prevStep : () => navigate("/my-assets")}>
                                    <i className="bi bi-arrow-left"></i> Back
                                  </button>
                                </div>
                              )}
                              <div className="mobile-nav-buttons">
                                {currentStep < 3 ? (
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={nextStep}
                                    disabled={!validateStep(values, errors)}
                                  >
                                    Next <i className="bi bi-arrow-right"></i>
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary me-3"
                                      onClick={() => handleSaveAsDraft(values)}
                                    >
                                      <i className="bi bi-pencil"></i> Save as Draft
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                      <i className="bi bi-floppy"></i> Save Asset
                                    </button>
                                  </>
                                )}
                              </div>
                            </>
                          ) : (
                            // Desktop navigation
                            <>
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate("/my-assets")}
                              >
                                <i className="bi bi-arrow-left"></i> Back
                              </button>
                              <div className="action-buttons-right">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary me-2"
                                  onClick={() => handleSaveAsDraft(values)}
                                >
                                  <i className="bi bi-pencil"></i> Save as Draft
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                  <i className="bi bi-floppy"></i> Save Asset
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </FormikForm>
                    )
                  }}
                </Formik>
              )}
        </div>
      </div>
    </Layout>
  )
}

const shouldShowIsActive = (subCategory) => {
  const showFor = ["insurance", "bank accounts", "vehicle"]
  return showFor.includes(subCategory)
}

export default AddAssets
